// Rechazos de una venta en un retailer integrado a Zentral y líneas LOST → mensaje y acción del vendedor.
import { ApiError } from '@/api/errors';
import integrationEsMx from '@/locales/es-MX/integration.json';
import type { ReservedLine, WizardSession } from './types';

const COPY = integrationEsMx.wizard;

/** `build-sale` = quitar o cambiar la pieza · `quote` = cotizar de nuevo · `cash` = pasar a contado. */
export type IntegrationRecovery = 'build-sale' | 'quote' | 'cash' | 'none';

export interface IntegrationFailure {
    code: IntegrationCode;
    message: string;
    recovery: IntegrationRecovery;
    /** Solo en RESERVATION_LOST: las líneas que Zentral se llevó. */
    lostLines?: { lineId: string; sku: string }[];
}

type IntegrationCode = keyof typeof COPY.codes;

const RECOVERY_BY_CODE: Record<IntegrationCode, IntegrationRecovery> = {
    RESERVATION_LOST: 'build-sale',
    PRODUCT_NO_LONGER_SELLABLE: 'build-sale',
    QUOTE_STALE: 'quote',
    STORE_NOT_OPERATIONAL: 'none',
    CREDIT_REQUIRES_MDM: 'cash',
};

export const INTEGRATION_ACTION_LABEL: Record<
    Exclude<IntegrationRecovery, 'none'>,
    string
> = COPY.actions;

/** Paso del wizard donde se resuelve cada acción. */
export const INTEGRATION_STEP = {
    'build-sale': 'build-sale',
    quote: 'quote',
    cash: 'payment-method',
} as const satisfies Record<Exclude<IntegrationRecovery, 'none'>, string>;

function isIntegrationCode(code: string): code is IntegrationCode {
    return Object.hasOwn(RECOVERY_BY_CODE, code);
}

function lostLinesFrom(data: unknown): { lineId: string; sku: string }[] {
    const lines = (data as { lines?: unknown } | null | undefined)?.lines;
    if (!Array.isArray(lines)) return [];
    return lines.flatMap((l: unknown) => {
        const { lineId, sku } = (l ?? {}) as { lineId?: unknown; sku?: unknown };
        return typeof lineId === 'string' && typeof sku === 'string' ? [{ lineId, sku }] : [];
    });
}

/** Salida de un rechazo de integración; `undefined` si el code no es de este contrato. */
export function integrationFailure(error: unknown): IntegrationFailure | undefined {
    if (!(error instanceof ApiError) || !isIntegrationCode(error.code)) return undefined;
    const code = error.code;
    return {
        code,
        message: COPY.codes[code],
        recovery: RECOVERY_BY_CODE[code],
        ...(code === 'RESERVATION_LOST' && { lostLines: lostLinesFrom(error.data) }),
    };
}

/** Líneas que Zentral vendió, traspasó o retiró: no se cobran, firman ni cierran. */
export function lostLinesOf(
    session: Pick<WizardSession, 'reservedLines'> | undefined,
): ReservedLine[] {
    return (session?.reservedLines ?? []).filter((l) => l.status === 'LOST');
}

/** «Moto G ya no se puede vender: Zentral ya la vendió.» */
export function lostLineText(name: string, reason: ReservedLine['lostReason']): string {
    const reasons: Record<string, string> = COPY.lostReason;
    const text =
        reason !== undefined && Object.hasOwn(reasons, reason) ? reasons[reason] : reasons.fallback;
    return COPY.lostLine.replace('{{name}}', name).replace('{{reason}}', text);
}

export const LOST_COPY = {
    badge: COPY.lostBadge,
    title: COPY.lostTitle,
    blocker: COPY.lostBlocker,
    remove: COPY.removeLost,
    choosePhone: COPY.choosePhone,
} as const;
